import React, { useState, useEffect } from "react";
import {
  Box,
  Form,
  Button,
  ResponsiveContext,
  Text,
  Layer,
  Spinner,
  Heading,
} from "grommet";

import ProductsSection from "../../components/ProficiencyPageComponents/ManagerProductsSection";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { addProduct } from "../../redux/slices/proficiencySlice";
import { useParams } from "react-router-dom";
import axios from "axios";
import API_URL from "../../config/config";
import HomeButtons from "../../components/Buttons/HomeButtons";
import { LinkPrevious } from "grommet-icons";
import { useSequence } from "../../components/SequenceProvider";
import BreadcrumbNavigation from "../../components/ManagerPortal/BreadcrumbNavigation2";

const AddProficiencyManager = () => {
  const dispatch = useDispatch();
  const { assessment_id } = useParams();
  const navigate = useNavigate();
  const role = useSelector((state) => state.profile.role);
  const { user_id } = useParams();
  const [products, setProducts] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [productSkills, setProductSkills] = useState([]);
  const [expertFlag, setExpertFlag] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [subProductSkills, setSubProductSkills] = useState([]);
  const [skillCoursesById, setSkillCoursesById] = useState({});
  const [productsToDelete, setProductsToDelete] = useState([]);
  const [subProductsToDelete, setSubProductsToDelete] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const selectedUsers = location.state?.selectedUsers || [];
  const { isSequenceCompleted, startSequence } = useSequence();
  const aiFactorySkillIds = [128, 129, 130];
  const courseSelectionStorageKey = `skillCourseSelections:${assessment_id}`;

  useEffect(() => {
    if (isSequenceCompleted) {
      // Redirect to dashboard if sequence is completed
      navigate(`/${user_id}/manager/assessments`, { replace: true });
    } else {
      // Start the sequence
      startSequence();
    }
  }, [isSequenceCompleted, navigate, startSequence]);
  // Fetch role skills, product skills, and sub-product skills
  const fetchSkillsData = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/getRolesAndSkills`);
      setProductSkills(response.data.product_skills);
      setSubProductSkills(response.data.sub_product_skills);
    } catch (error) {
      console.error("Error fetching skills data:", error);
    }
  };

  const fetchSkillCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/skill-courses`, {
        params: { skillIds: aiFactorySkillIds.join(",") },
      });
      const coursesBySkill = response.data.coursesBySkill || {};
      const courseMap = Object.fromEntries(
        Object.entries(coursesBySkill).map(([skillId, courses]) => [
          skillId,
          (courses || []).map((course) => ({
            course_id: course.course_id,
            course_name: course.course_name,
          })),
        ]),
      );
      setSkillCoursesById(courseMap);
    } catch (error) {
      console.error("Error fetching skill courses:", error);
    }
  };
  useEffect(() => {
    fetchSkillsData();
    fetchSkillCourses();
  }, []); // This runs only once to fetch role and product skills

  useEffect(() => {
    const selections = products.reduce((acc, product) => {
      if (Array.isArray(product.courseIds) && product.courseIds.length > 0) {
        acc[product.subProductSkillId] = product.courseIds;
      }
      return acc;
    }, {});
    try {
      if (Object.keys(selections).length === 0) {
        localStorage.removeItem(courseSelectionStorageKey);
      } else {
        localStorage.setItem(
          courseSelectionStorageKey,
          JSON.stringify(selections),
        );
      }
    } catch (error) {
      console.error("Error storing course selections:", error);
    }
  }, [courseSelectionStorageKey, products]);

  const [formData, setFormData] = useState({
    currentRole: role,
    totalExperience: "",
    productCategory: "",
    productName: "",
    productLevel: "",
    providerName: "",
    certificateName: "",
    achievementDate: "",
    expiryDate: "",
    neverExpires: false,
    certificateId: "",
    certificateUrl: "",
  });

  const size = React.useContext(ResponsiveContext);

  const handleInputChange = (name, value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleExpertSelection = (isExpert) => {
    setExpertFlag(isExpert);
  };

  // Adjust handleDeleteProduct to keep track and manage deletion accurately
  const handleDeleteProduct = (index) => {
    const productToDelete = products[index];

    // Find the sub-product based on the subProductName in subProductSkills
    const subProductSkill = subProductSkills.find(
      (subProduct) =>
        subProduct.skill_name === productToDelete.subProductName &&
        subProduct.product_name === productToDelete.productName,
    );

    if (subProductSkill) {
      setSubProductsToDelete([...subProductsToDelete, subProductSkill.id]);
    }

    setProducts(products.filter((_, i) => i !== index));
    setProductsToDelete([...productsToDelete, productToDelete]); // Add to deletion list
  };

  // When a new product is added, ensure productsToDelete is cleared if no products remain to be deleted
  useEffect(() => {
    if (products.length === 0) {
      setProductsToDelete([]); // Clear productsToDelete when no products are present
    }
  }, [products]);

  const handleSubmit = async (event) => {
    setLoading(true);
    event.preventDefault();

    // Validation checks
    if (products.length > 6) {
      setAlertMessage("Error! You can only add your top 6 skills.");
      setShowAlert(true);
      setLoading(false);
      return;
    }

    if (products.length === 0) {
      setAlertMessage(
        "Please add at least one product before submitting the form.",
      );
      setShowAlert(true);
      setLoading(false);
      return;
    }

    setAlertMessage("");
    setShowAlert(false);

    const productIds = products.map((product) => product.id);
    const productDetails = products
      .filter((product) => product.subProductSkillId) // Include only products with subProductSkillId
      .map((product) => ({
        subProductSkillId: product.subProductSkillId,
        productLevel: product.productLevel,
      }));

    const fullFormData = {
      selectedUsers, // Array of selected user IDs
      assessment_id, // Assessment ID
      products: productIds, // Product IDs
      productDetails, // Sub-product skill details
    };

    try {
      // Send data to the API
      const response = await axios.post(`${API_URL}/manager/addUserHistory`, {
        fullFormData,
      });
      setLoading(false);

      if (response.status === 201) {
        // Navigate to quiz settings page after successful submission
        navigate(`/manager/${assessment_id}/quiz-settings`, {
          state: {
            user_id,
            selectedUsers,
            expertFlag,
          },
        });
      }
    } catch (error) {
      console.error("Error handling profile update:", error);
      setLoading(false);
      setAlertMessage(
        "An error occurred while submitting the form. Please try again.",
      );
      setShowAlert(true);
    }
  };

  if (loading) {
    return (
      <Box fill align="center" justify="center">
        <Spinner size="large" />
        <Text>Processing...</Text>
      </Box>
    );
  }
  return (
    <Box fill="vertical" justify="between">
      <HeaderNavigation />
      <Box flex="grow" pad="medium">
        <Box
          margin={{ bottom: "small" }}
          pad="small"
          direction="row"
          align="center"
          justify="between"
          // background="light-2"
          // round="small"
          // elevation="small"
          height="50px"
        >
          {/* Left side: Back Arrow + Title */}
          <Box direction="row" align="center" gap="small">
            <LinkPrevious
              size="large"
              onClick={() => navigate(-1)}
              color="brand"
            />
            <BreadcrumbNavigation
              current={"Step 2: Select Skills for Team Assessment"}
              previous={"Select Users"}
            />
          </Box>

          {/* Right side: Home Buttons */}
          <HomeButtons userid={user_id} flag={1} />
        </Box>
        {/* <Box
          border={{ color: "brand", size: "xsmall" }}
          width="75%"
          alignSelf="end"
          margin={{ top: "small" }}
        /> */}
        <Box align="center" justify="center" pad="medium">
          <Form onSubmit={handleSubmit}>
            <Box pad="small" gap="medium">
              <ProductsSection
                size={size}
                formData={formData}
                setFormData={setFormData}
                handleInputChange={handleInputChange}
                products={products}
                setProducts={setProducts}
                editIndex={editIndex}
                setEditIndex={setEditIndex}
                productSkills={productSkills}
                subProductSkills={subProductSkills}
                skillCoursesById={skillCoursesById}
                setSubProductSkills={setSubProductSkills}
                handleExpertSelection={handleExpertSelection}
                handleDeleteProduct={handleDeleteProduct} // Add this prop
              />
            </Box>
            <Box margin={{ top: "medium" }} width="100%" align="center">
              <Button
                type="submit"
                label="Continue"
                primary
                disabled={products.length === 0}
              />
            </Box>
          </Form>
        </Box>
      </Box>
      <StickyFooter />

      {showAlert && (
        <Layer
          onEsc={() => setShowAlert(false)}
          onClickOutside={() => setShowAlert(false)}
        >
          <Box pad="medium" gap="small">
            <Text>{alertMessage}</Text>
            <Box direction="row" gap="medium" justify="center">
              <Button label="Cancel" onClick={() => setShowAlert(false)} />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default AddProficiencyManager;
